import { createColumnHelper } from '@tanstack/react-table';
import { ChevronDownIcon, ChevronUpIcon, X } from 'lucide-react';
import { useMemo, useState } from 'react';

import { useGetAllClasses } from '~entities/classes';

import {
  EnrollioTable,
  EnrollioTableSearch,
} from '~shared/components/enrollio-table';
import { defaultStyles } from '~shared/components/select/helper';
import Select from '~shared/components/select/select';
import { Button } from '~shared/shadcn/ui/button';

interface ClassOption {
  label: string;
  value: string;
}

interface ExcludedClassesTableProps {
  selectedClasses: Array<string>;
  setSelectedClasses: (classes: Array<string>) => void;
}

interface ExcludedClassRow {
  id: string;
  title: string;
  session: string;
}

const columnHelper = createColumnHelper<ExcludedClassRow>();

export function ExcludedClassesTable({
  selectedClasses,
  setSelectedClasses,
}: ExcludedClassesTableProps) {
  const [isOpen, setIsOpen] = useState(true);
  const [pendingClasses, setPendingClasses] = useState<Array<ClassOption>>([]);
  const { data: classes, isFetching } = useGetAllClasses();
  const classOptions =
    classes?.flatMap((classItem) => {
      const sessionName = classItem.session?.name;
      if (!sessionName) {
        return [];
      }
      return [
        {
          label: `${classItem.title} - ${sessionName}`,
          value: classItem._id,
        },
      ];
    }) ?? [];
  const rows = useMemo(
    () =>
      selectedClasses.flatMap((classId) => {
        const classItem = classes?.find((item) => item._id === classId);
        if (!classItem) {
          return [];
        }
        return [
          {
            id: classId,
            title: classItem.title,
            session: classItem.session?.name ?? 'No session',
          },
        ];
      }),
    [classes, selectedClasses],
  );
  const columns = useMemo(
    () => [
      columnHelper.accessor('title', {
        header: 'Class name',
        cell: (info) => <span className='font-medium'>{info.getValue()}</span>,
      }),
      columnHelper.accessor('session', { header: 'Session' }),
      columnHelper.display({
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <Button
            type='button'
            variant='ghost'
            size='icon'
            aria-label={`Remove ${row.original.title}`}
            onClick={() => {
              setSelectedClasses(
                selectedClasses.filter((id) => id !== row.original.id),
              );
            }}
          >
            <X className='size-4' />
          </Button>
        ),
      }),
    ],
    [selectedClasses, setSelectedClasses],
  );

  return (
    <div className='mt-8'>
      <button
        onClick={() => {
          setIsOpen(!isOpen);
        }}
        className='mb-4 flex w-full items-center justify-between rounded-lg bg-white px-4 py-3 text-left shadow'
      >
        <h3 className='text-lg font-medium text-gray-900'>Excluded Classes</h3>
        {isOpen ? (
          <ChevronUpIcon className='size-5 text-gray-500' />
        ) : (
          <ChevronDownIcon className='size-5 text-gray-500' />
        )}
      </button>

      <div
        className={`overflow-hidden transition-all duration-200 ease-in-out ${
          isOpen ? 'max-h-[100000px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div
          className={`rounded-lg border border-gray-200 bg-white shadow ${
            isOpen ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <div className='p-4'>
            <EnrollioTable
              totalLabel='Excluded classes'
              filterHeader={(table) => (
                <EnrollioTableSearch
                  table={table}
                  placeholder='Search excluded classes...'
                />
              )}
              toolbar={
                <div className='flex min-w-[300px] flex-1 items-start gap-2'>
                  <Select
                    isMulti
                    value={pendingClasses}
                    onChange={(newValue: unknown) => {
                      setPendingClasses(
                        (newValue as Array<ClassOption> | null) ?? [],
                      );
                    }}
                    options={classOptions.filter(
                      (option) => !selectedClasses.includes(option.value),
                    )}
                    placeholder='Select classes to exclude...'
                    isLoading={isFetching}
                    className='min-w-0 flex-1 text-sm'
                    classNamePrefix='select'
                    isClearable
                    closeMenuOnSelect={false}
                    maxMenuHeight={280}
                    styles={defaultStyles}
                    menuPosition='fixed'
                    menuPortalTarget={
                      typeof document !== 'undefined' ? document.body : null
                    }
                  />
                  <Button
                    type='button'
                    disabled={pendingClasses.length === 0}
                    onClick={() => {
                      setSelectedClasses([
                        ...selectedClasses,
                        ...pendingClasses.map((option) => option.value),
                      ]);
                      setPendingClasses([]);
                    }}
                  >
                    Add
                  </Button>
                </div>
              }
              data={rows}
              columns={columns}
              getRowId={(row) => row.id}
              isUpdating={isFetching}
              emptyContent='No excluded classes.'
              ariaLabel='Excluded classes'
            />
          </div>
        </div>
      </div>
    </div>
  );
}
